import { Close } from "@mui/icons-material";
import { CircularProgress, Drawer, IconButton } from "@mui/material";
import LeadsSection from "./LeadsSection";
import StatTile from "./StatTile";
import StatusChip from "./StatusChip";
import { useCampaignDetail } from "./useMarketing";
import {
  clickThroughRate,
  currencyOf,
  EFFECTIVE_STATUS_HELP,
  formatCount,
  formatMoney,
  labelOf,
} from "./marketingFormat";

function Fact({ label, children }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-app-text-secondary">{label}</dt>
      <dd className="text-sm text-app-text">{children}</dd>
    </div>
  );
}

function History({ rows, currency, resultLabel }) {
  if (rows.length === 0) {
    return (
      <p className="text-sm text-app-text-secondary">
        No results recorded yet. Use Refresh to pull them from Meta.
      </p>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase text-app-text-secondary">
            <th className="py-1 pr-3">As of</th>
            <th className="py-1 pr-3">Spend</th>
            <th className="py-1 pr-3">Impressions</th>
            <th className="py-1 pr-3">Reach</th>
            <th className="py-1 pr-3">Clicks</th>
            <th className="py-1">{resultLabel}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.capturedOn} className="border-t border-app-border-subtle">
              <td className="py-1 pr-3">{row.capturedOn}</td>
              <td className="py-1 pr-3">{formatMoney(row.spendCents, currency)}</td>
              <td className="py-1 pr-3">{formatCount(row.impressions)}</td>
              <td className="py-1 pr-3">{formatCount(row.reach)}</td>
              <td className="py-1 pr-3">{formatCount(row.clicks)}</td>
              <td className="py-1">{formatCount(row.results)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function CampaignDetailDrawer({
  campaignId,
  connections,
  actions,
  onClose,
  canReadLeads,
}) {
  const { data, isLoading, isError } = useCampaignDetail(campaignId);
  const campaign = data?.campaign;
  const currency = currencyOf(connections, campaign?.connectionId);
  return (
    <Drawer anchor="right" open={campaignId != null} onClose={onClose}>
      <div className="w-screen max-w-xl space-y-5 bg-app-surface p-5 text-app-text">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-lg font-semibold">{campaign?.name || "Campaign"}</h2>
          <IconButton aria-label="Close" onClick={onClose} size="small">
            <Close />
          </IconButton>
        </div>
        {isLoading ? <CircularProgress size={24} /> : null}
        {isError ? (
          <p className="text-sm text-app-status-cancelled-text">Could not load this campaign.</p>
        ) : null}
        {campaign ? (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <StatusChip campaign={campaign} />
              {actions(campaign)}
            </div>
            {EFFECTIVE_STATUS_HELP[campaign.effectiveStatus] ? (
              <p className="rounded bg-app-surface-alt p-3 text-sm">
                {EFFECTIVE_STATUS_HELP[campaign.effectiveStatus]}
              </p>
            ) : null}
            <div className="grid grid-cols-2 gap-3">
              <StatTile label="Spend" value={formatMoney(campaign.spendCents, currency)} />
              <StatTile label={campaign.resultLabel} value={formatCount(campaign.results)} />
              <StatTile label="Impressions" value={formatCount(campaign.impressions)} />
              <StatTile label="Reach" value={formatCount(campaign.reach)} />
              <StatTile label="Clicks" value={formatCount(campaign.clicks)} />
              <StatTile
                label="Click-through"
                value={clickThroughRate(campaign.clicks, campaign.impressions)}
              />
            </div>
            <dl className="grid grid-cols-2 gap-3">
              <Fact label="Objective">{campaign.objective?.replace("OUTCOME_", "")}</Fact>
              <Fact label="Daily budget">
                {campaign.dailyBudgetCents > 0
                  ? formatMoney(campaign.dailyBudgetCents, currency)
                  : "Set on the ad set in Meta"}
              </Fact>
              <Fact label="Runs for">
                {campaign.durationDays
                  ? `${campaign.durationDays} days from each start`
                  : "Until paused"}
              </Fact>
              {connections.length > 1 ? (
                <Fact label="Account">{labelOf(connections, campaign.connectionId)}</Fact>
              ) : null}
              <Fact label="Meta campaign id">{campaign.externalId}</Fact>
              {campaign.sourcePostId ? (
                <Fact label="Boosted post">{campaign.sourcePostId}</Fact>
              ) : null}
              {campaign.insightsAsOf ? (
                <Fact label="Results as of">{campaign.insightsAsOf}</Fact>
              ) : null}
            </dl>
            {canReadLeads && campaign.objective === "OUTCOME_LEADS" ? (
              <LeadsSection campaignId={campaign.id} />
            ) : null}
            <section>
              <h3 className="mb-2 font-semibold">Results over time</h3>
              <p className="mb-2 text-xs text-app-text-muted">
                Lifetime totals captured each time results are refreshed.
              </p>
              <History rows={data.history} currency={currency} resultLabel={campaign.resultLabel} />
            </section>
          </>
        ) : null}
      </div>
    </Drawer>
  );
}
